import { ArrowLeft, BarChart3, ChevronDown, History, ListChecks, Play, Plus, Target } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ApiError } from '../../lib/api.ts';
import { modeLabel } from '../../lib/modes.ts';
import type { RunState, Workout } from '../../lib/planning.ts';
import { getCurrentRun, listWorkouts, startWorkout } from '../../lib/planning.ts';
import { describe } from './WorkoutComposer.tsx';

/* Mes séances.
 *
 * Une page à elles, et non un bloc du menu Entraînement : à partir de trois ou
 * quatre séances, la liste repoussait « Exercices » sous la ligne de
 * flottaison, et une entrée qu'on ne voit plus est une entrée qui n'existe
 * plus. Le menu garde deux portes de même rang ; ce qui grandit grandit ici.
 */

const dayLabel = (iso: string): string =>
    new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' });

export function WorkoutList() {
    const navigate = useNavigate();
    const [workouts, setWorkouts] = useState<Workout[]>([]);
    const [run, setRun] = useState<RunState['run']>(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    /** Séances dépliées. En mémoire seulement : au practice on ouvre une
     *  séance, on la lance, et on ne revient pas comparer. */
    const [open, setOpen] = useState<Record<string, boolean>>({});

    const load = useCallback(async () => {
        try {
            const [w, r] = await Promise.all([listWorkouts(), getCurrentRun()]);
            setWorkouts(w);
            setRun(r.run);
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Chargement impossible');
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { void load(); }, [load]);

    const onStart = async (id: string): Promise<void> => {
        setBusy(true); setError(null);
        try {
            await startWorkout(id);
            void navigate('/entrainement/seance-en-cours');
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Démarrage impossible');
            setBusy(false);
        }
    };

    if (loading) {
        return <div className="flex justify-center py-16"><span className="loading loading-ring loading-lg text-primary" /></div>;
    }

    return (
        <div className="space-y-4 py-2">
            <header>
                <Link to="/entrainement" className="btn btn-ghost btn-sm mb-2 gap-2">
                    <ArrowLeft size={16} /> Entraînement
                </Link>
                <div className="flex items-baseline justify-between gap-2">
                    <h1 className="text-2xl font-bold tracking-tight">Mes séances</h1>
                    <Link to="/entrainement/seance/nouvelle" className="btn btn-ghost btn-sm gap-1 tap-target">
                        <Plus size={16} /> Ajouter
                    </Link>
                </div>
            </header>

            {error && <div role="alert" className="alert alert-error text-sm"><span>{error}</span></div>}

            {workouts.length === 0 ? (
                <div className="rounded-box bg-base-200 p-5">
                    <ListChecks size={26} className="text-primary" />
                    <p className="mt-2 font-semibold">Préparez votre practice à l'avance</p>
                    <p className="mt-1 text-sm opacity-70">
                        Une séance est une suite d'exercices que vous écrivez une fois,
                        au calme, et que vous n'avez plus qu'à dérouler sur le tapis.
                    </p>
                    <Link to="/entrainement/seance/nouvelle"
                        className="btn btn-primary mt-4 gap-2 tap-target">
                        <Plus size={18} /> Créer ma première séance
                    </Link>
                </div>
            ) : (
                <ul className="space-y-3">
                    {workouts.map((w) => (
                        <li key={w.id} className="rounded-box bg-base-200 p-4">
                            <div className="flex items-start gap-3">
                                <span className="min-w-0 flex-1">
                                    <span className="block font-bold">{w.name}</span>
                                    {w.description && (
                                        <span className="block truncate text-sm opacity-60">{w.description}</span>
                                    )}
                                </span>
                                <Link to={`/entrainement/seance/${w.id}`}
                                    className="btn btn-ghost btn-sm shrink-0 tap-target">
                                    Modifier
                                </Link>
                            </div>

                            {/* Déplier le contenu sans quitter la page.
                                « Modifier » y menait déjà, mais ouvre un
                                compositeur : trop pour la seule question
                                « qu'est-ce qu'il y a dedans ? », qu'on se pose
                                justement en choisissant sa séance sur le
                                parking.

                                Toute la ligne est la cible, sur 48 px de haut :
                                au practice on touche avec un gant, et un
                                chevron de 16 px ne se vise pas. */}
                            <button type="button"
                                onClick={() => setOpen((s) => ({ ...s, [w.id]: !s[w.id] }))}
                                aria-expanded={open[w.id] ?? false}
                                className="mt-2 flex w-full items-center gap-2 rounded-field px-2 py-3 text-left transition-colors hover:bg-base-300">
                                <span className="min-w-0 flex-1 text-xs opacity-70">
                                    {w.items.length} exercice{w.items.length > 1 ? 's' : ''}
                                    {/* Les deux chiffres qui décident du choix :
                                        quand, et comment ça s'était passé. Le
                                        taux ne s'affiche que s'il existe — un
                                        « 0 % » pour « jamais fait » serait un
                                        mensonge décourageant. */}
                                    {w.lastRunAt
                                        ? ` · dernière fois le ${dayLabel(w.lastRunAt)}`
                                        : ' · jamais faite'}
                                    {/* Le dernier TOTAL quand la séance se note en
                                        points : un examen ne se retient pas en
                                        proportion, et « 80 % de réussite » ne dit
                                        pas si le test a été obtenu. */}
                                    {w.pointsTarget != null && w.lastRunPoints != null
                                        ? ` · ${w.lastRunPoints} point${w.lastRunPoints > 1 ? 's' : ''} sur ${w.pointsMax}`
                                        : w.successRate != null
                                            && ` · ${Number(w.successRate).toFixed(0)} % de réussite`}
                                </span>
                                <ChevronDown size={20}
                                    className={`shrink-0 opacity-60 transition-transform ${open[w.id] ? 'rotate-180' : ''}`} />
                            </button>

                            {open[w.id] && (
                                <ol className="mt-1 space-y-1 border-t border-base-300 pt-2">
                                    {w.items.map((it) => (
                                        <li key={it.id} className="flex items-center gap-2 px-2 py-1.5 text-sm">
                                            <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-base-300 text-xs font-bold">
                                                {it.position}
                                            </span>
                                            <span className="min-w-0 flex-1 truncate">
                                                {it.isPutting
                                                    ? <span className="inline-flex items-center gap-1"><Target size={14} /> Putting</span>
                                                    : it.exerciseName ?? modeLabel(it.mode ?? '')}
                                            </span>
                                            <span className="shrink-0 text-xs opacity-60">
                                                {it.isPutting ? 'Sur le green'
                                                    : it.mode ? 'Intégré'
                                                        : describe(it.ballsPerRound, it.rounds)}
                                            </span>
                                        </li>
                                    ))}
                                    {w.lastRunId && (
                                        <li className="flex flex-wrap gap-1 pt-1">
                                            <Link to={`/entrainement/bilan/${w.lastRunId}`}
                                                className="btn btn-ghost btn-sm gap-2 tap-target">
                                                <BarChart3 size={16} /> Bilan de la dernière fois
                                            </Link>
                                            {/* Le bilan compare deux exécutions ; les
                                                passages les montrent toutes. Le lien
                                                n'apparaît qu'à partir de la deuxième :
                                                une progression sur un seul point
                                                n'existe pas. */}
                                            {(w.runCount ?? 0) > 1 && (
                                                <Link to={`/entrainement/passages/${w.id}`}
                                                    className="btn btn-ghost btn-sm gap-2 tap-target">
                                                    <History size={16} /> Mes {w.runCount} passages
                                                </Link>
                                            )}
                                        </li>
                                    )}
                                </ol>
                            )}

                            <button type="button"
                                disabled={busy || run !== null || w.items.length === 0}
                                onClick={() => void onStart(w.id)}
                                className="btn btn-primary btn-block mt-3 h-14 gap-2 text-lg font-bold tap-target">
                                <Play size={20} /> Démarrer
                            </button>
                            {w.items.length === 0 && (
                                <p className="mt-1 text-center text-xs opacity-60">
                                    Ajoutez au moins un exercice pour pouvoir la lancer.
                                </p>
                            )}
                            {run !== null && w.items.length > 0 && (
                                <p className="mt-1 text-center text-xs opacity-60">
                                    Une séance est déjà en cours.
                                </p>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
